import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { BaseVm } from '@/mobxTools'
import {
  MAX_BENEFITS,
  PRO_BENEFITS,
  PRO_V_BENEFITS,
  ULTRA_BENEFITS,
} from '@/pages/PricingPage/pricingConsts'
import { buyVipService } from '@/services/buyVipService'
import { userService } from '@/services/userService'
import { computed, observable } from 'mobx'
import { BuyVipDialogV1Props } from '.'

export interface Plan extends ServerDataTypes.VipPlan {
  id: string
  title?: string
  titleWechat?: string
  qrTitle?: string
  tokenCount: number
  recommend?: boolean
  priceStr: number
  monthPrice?: number
  currentPrice?: number
  originalPriceStr: string
  isSubscription?: boolean
  priceDesc?: string
}
export class BuyVipDialogV1Vm extends BaseVm<BuyVipDialogV1Props> {
  props!: BuyVipDialogV1Props

  @observable selectPlan!: ServerDataTypes.FormatPlan
  @observable hasDiscountPlan = false
  @observable currentTabKey: 'wx_pub_qr' | 'alipay_qr' = 'alipay_qr'
  @observable orderCreating = true
  @observable orderQrCode = ''
  @observable qrErr = false
  @observable discountEndTime!: number
  @observable funcList: ServerDataTypes.BuyVipDialogExtraData['featureList'] = []
  @observable curFuncKey = 'web'
  @observable vipGiftList: ServerDataTypes.BuyVipDialogExtraData['vipGiftList'] = []
  @observable curVipType: 'pro' | 'pro+' | 'max' | 'ultra' = 'pro'
  @observable alipayAgree = false
  @observable proBenefits: ServerDataTypes.BenefitsInfo = PRO_BENEFITS
  @observable proVBenefits: ServerDataTypes.BenefitsInfo = PRO_V_BENEFITS
  @observable maxBenefits: ServerDataTypes.BenefitsInfo = MAX_BENEFITS
  @observable ultraBenefits: ServerDataTypes.BenefitsInfo = ULTRA_BENEFITS
  // @observable showPro = false

  @observable scale = 1

  @computed get benefits() {
    if (this.curVipType === 'pro+') {
      return this.proVBenefits
    }
    if (this.curVipType === 'max') {
      return this.maxBenefits
    }
    if (this.curVipType === 'ultra') {
      return this.ultraBenefits
    }
    return this.proBenefits
  }
  @computed get vipPlans() {
    if (this.curVipType === 'pro+') {
      return commonUtils.jsonClone(this.proPlusVipPlans)
    }
    if (this.curVipType === 'max') {
      return commonUtils.jsonClone(this.maxVipPlans)
    }
    if (this.curVipType === 'ultra') {
      return commonUtils.jsonClone(this.ultraVipPlans)
    }
    return commonUtils.jsonClone(this.proVipPlans)
  }
  private sessionId?: string
  @observable proVipPlans: ServerDataTypes.FormatPlan[] = []
  @observable proPlusVipPlans: ServerDataTypes.FormatPlan[] = []
  @observable maxVipPlans: ServerDataTypes.FormatPlan[] = []
  @observable ultraVipPlans: ServerDataTypes.FormatPlan[] = []
  private timer?: any

  public async onViewCreate() {
    await this.checkAlipaySignStatus()
    this.initPlansData()
    this.initExtraData()
    try {
      const {
        versionData: { alipayAgree },
      } = userService.userData!

      userService.postHuiyiRecord({
        module: '付费弹窗',
        remark: {
          version: userService.vipVersion,
          id: userService.userData?.inviteCode ?? '',
        },
      })
      this.alipayAgree = alipayAgree
    } catch (error) {}

    this.calcHeight()
    window.addEventListener('resize', this.calcHeight.bind(this))
  }
  public onViewDestroy(): void {
    // view 被销毁后执行
    this.clearTimeout()
    this.sessionId = undefined
    window.removeEventListener('resize', this.calcHeight)
  }

  public transferToStr = (count: number) => {
    return count.toLocaleString()
  }

  public transferTokenCount = (cnt: number) => {
    return `${Math.floor(cnt / 10000)}W`
  }

  public handleChangeVipType(type: 'pro' | 'pro+' | 'max' | 'ultra') {
    if (this.curVipType !== type) {
      this.curVipType = type
      if (type === 'pro') {
        this.selectPlan = this.proVipPlans[0]
      } else if (type === 'pro+') {
        this.selectPlan = this.proPlusVipPlans[0]
      } else if (type === 'max') {
        this.selectPlan = this.maxVipPlans[0]
      } else if (type === 'ultra') {
        this.selectPlan = this.ultraVipPlans[0]
      }
      this.genOrderQrCode()
    }
  }

  public async genOrderQrCode() {
    if (!userService.isLogin) {
      console.error('未找到用户登录态')
      return
    }
    if (this.alipayAgree && this.selectPlan.isSubscription && this.currentTabKey === 'alipay_qr') {
      this.qrErr = true
      this.orderCreating = false
      this.orderQrCode =
        this.orderQrCode ||
        'https://assets.weibanzhushou.com/web/pt-web/%E4%BC%9A%E8%AF%91-%E8%81%94%E7%B3%BB%E5%AE%A2%E6%9C%8D.126a016dd9f4.jpeg'
      return
    }
    this.orderCreating = true
    this.qrErr = false
    const sessionId = commonUtils.genId()
    this.sessionId = sessionId
    /** 取消上一次轮询 */
    this.clearTimeout()
    try {
      const { qr, orderId } = await buyVipService.createOrder({
        action: userService.userData?.versionData.version === 'free' ? 'buy' : 'renew',
        chargeChannel:
          this.selectPlan.isSubscription && this.currentTabKey === 'alipay_qr'
            ? 'alipay_agreement_qr'
            : this.currentTabKey,
        planId: this.selectPlan.id,
        version: this.selectPlan.version,
        source: `${this.props.source}`,
      })
      if (this.sessionId !== sessionId) {
        return
      }
      this.orderQrCode = qr
      this.pollingOrderStatus(orderId, true)
    } catch (error) {
      console.log(error)
      this.qrErr = true
    }
    this.orderCreating = false
  }

  handlePriceChange(): void {
    if (this.selectPlan.isSubscription) {
      this.currentTabKey = 'alipay_qr'
    }
    this.genOrderQrCode()
  }

  async onTimerEnd() {
    let { planList, proPlusPlanList, showPro, maxPlanList, ultraPlanList } =
      await api.pricing.list()

    this.processPlanList(showPro, planList, proPlusPlanList, maxPlanList, ultraPlanList)
  }

  private async initExtraData() {
    const { vipGiftList, featureList, proBenefits, proVBenefits, maxBenefits } =
      await buyVipService.getBuyVipDialogExtraData()
    this.funcList = featureList
    this.curFuncKey = featureList[0].key
    this.vipGiftList = vipGiftList
    this.proBenefits = proBenefits
    this.proVBenefits = proVBenefits
    this.maxBenefits = maxBenefits
  }

  private formatPlan(plan: ServerDataTypes.VipPlan) {
    return commonUtils.formatDiscountPlan(plan)
    // return {
    //   ...plan,
    //   title: this.transTitle(plan.activeTime, plan.timeType),
    //   titleWechat: this.transWechatTitle(plan.activeTime, plan.timeType),
    //   qrTitle: this.transQrTitle(plan.activeTime, plan.timeType, plan.version),
    //   priceStr: Math.floor(plan.price / 100),

    //   monthPrice: Number(commonUtils.parsePriceStr(plan.unitPrice, false)),
    //   currentPrice: Number(commonUtils.parsePriceStr(plan.price, false)),
    //   recommend: plan.timeType === 'year',
    //   per: Math.floor(plan.price / (plan.activeTime * 100)),
    //   originalPriceStr: Math.floor(plan.originPrice / 100).toString(),
    //   isSubscription: SUBSCRIPTION_TYPE.includes(plan.timeType),
    //   priceDesc: this.formatPriceDesc(plan),
    // }
  }

  private async initPlansData() {
    try {
      const { planList, matchType, proPlusPlanList, showPro, maxPlanList, ultraPlanList } =
        await buyVipService.list()

      this.processPlanList(showPro, planList, proPlusPlanList, maxPlanList, ultraPlanList)
      this.initDiscountInfo(matchType)
    } catch (error) {
      console.error('获取套餐列表失败')
    }
  }

  // eslint-disable-next-line max-len
  private processPlanList(
    showPro: boolean,
    planList: ServerDataTypes.VipPlan[],
    proPlusPlanList: ServerDataTypes.VipPlan[],
    maxPlanList: ServerDataTypes.VipPlan[],
    ultraPlanList: ServerDataTypes.VipPlan[]
  ) {
    console.log({
      showPro,
      planList,
      proPlusPlanList,
      maxPlanList,
      ultraPlanList,
    })
    // this.showPro = showPro

    this.proVipPlans = planList.map((plan) => {
      return this.formatPlan(plan)
    })
    this.proPlusVipPlans = proPlusPlanList.map((plan) => {
      return this.formatPlan(plan)
    })
    this.ultraVipPlans = ultraPlanList.map((plan) => {
      return this.formatPlan(plan)
    })
    this.maxVipPlans = maxPlanList.map((plan) => {
      return this.formatPlan(plan)
    })
    // v4接口只有max 和 ultra 版本了

    if (this.props.version === 'ultra') {
      this.curVipType = 'ultra'
      let findPlan = this.ultraVipPlans.find((v) => {
        if (this.props.selectedPlanId) {
          return v.id === this.props.selectedPlanId
        }
      })
      if (!findPlan) {
        findPlan = this.ultraVipPlans.find((v) => {
          return v.newUserDiscount
        })
      }

      this.selectPlan = findPlan || this.ultraVipPlans[0]
    } else if (this.props.version === 'max') {
      this.curVipType = 'max'
      let findPlan = this.maxVipPlans.find((v) => {
        if (this.props.selectedPlanId) {
          return v.id === this.props.selectedPlanId
        }
      })
      if (!findPlan) {
        findPlan = this.maxVipPlans.find((v) => {
          return v.newUserDiscount
        })
      }
      this.selectPlan = findPlan || this.maxVipPlans[0]
    } else {
      this.curVipType = 'ultra'
      let findPlan = this.ultraVipPlans.find((v) => {
        if (this.props.selectedPlanId) {
          return v.id === this.props.selectedPlanId
        }
      })
      if (!findPlan) {
        findPlan = this.ultraVipPlans.find((v) => {
          return v.newUserDiscount
        })
      }

      this.selectPlan = findPlan || this.ultraVipPlans[0]
    }

    // if (!showPro) {
    //   this.curVipType = 'max'

    //   let findPlan = this.maxVipPlans.find((v) => {
    //     if (this.props.selectedPlanId) {
    //       return v.id === this.props.selectedPlanId
    //     }
    //   })

    //   if (!findPlan) {
    //     findPlan = this.maxVipPlans.find((v) => {
    //       return v.newUserDiscount
    //     })
    //   }

    //   this.selectPlan = findPlan || this.maxVipPlans[0]
    // } else {
    //   // 有pro和pro+的情况
    //   if (this.props.version === 'pro') {
    //     this.curVipType = 'pro'
    //     let findPlan = this.proVipPlans.find((v) => {
    //       if (this.props.selectedPlanId) {
    //         return v.id === this.props.selectedPlanId
    //       }
    //     })
    //     if (!findPlan) {
    //       findPlan = this.proVipPlans.find((v) => {
    //         return v.newUserDiscount
    //       })
    //     }

    //     this.selectPlan = findPlan || this.proVipPlans[0]
    //   } else {
    //     this.curVipType = 'max'
    //     let findPlan = this.maxVipPlans.find((v) => {
    //       if (this.props.selectedPlanId) {
    //         return v.id === this.props.selectedPlanId
    //       }
    //     })
    //     if (!findPlan) {
    //       findPlan = this.maxVipPlans.find((v) => {
    //         return v.newUserDiscount
    //       })
    //     }
    //     this.selectPlan = findPlan || this.maxVipPlans[0]
    //   }
    // }

    this.hasDiscountPlan = !!this.selectPlan.newUserDiscount
    this.handlePriceChange()
  }

  private async pollingOrderStatus(orderId: string, force = false, cnt = 0) {
    if (!this.props.open) {
      return
    }
    if (force) {
      try {
        const { status } = await buyVipService.pollOrderStatus(orderId)
        if (!this.sessionId) {
          return
        }
        if (status === 'completed') {
          await userService.updateUserInfo()
          if (this.props.paySuccess) {
            this.props.paySuccess(this.selectPlan.timeType === 'year')
          }
          setTimeout(() => {
            if (this.props.onClose) {
              this.props.onClose(true)
            }
          })
          /** 停止轮询,给出成功提示 */
          this.clearTimeout()
          try {
            commonUtils.sendExtensionMessage('markPaid', {})
          } catch (error) {}
          return
        }
        if (status === 'canceled' || status === 'refunded') {
          /** 停止轮询 */
          return
        }
        if (status === 'created') {
          this.timer = setTimeout(() => {
            this.pollingOrderStatus(orderId, true, 0)
          }, 1000)
        }
      } catch (error) {
        if (cnt > 5) {
          this.clearTimeout()
          this.qrErr = true
          /** 重试结束，停止轮询 */
          return
        }
        this.pollingOrderStatus(orderId, true, cnt + 1)
      }
    }
  }

  private clearTimeout() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  }

  private initDiscountInfo(matchType: ServerDataTypes.WelfareMatchType) {
    if (!userService.isLogin) {
      return
    }
    const {
      newUserDiscount,
      createdAt,
      versionData: { endTime },
    } = userService.userData!
    if (newUserDiscount) {
      this.discountEndTime = createdAt * 1000 + 48 * 60 * 60 * 1000
    } else if (matchType === 'exchange_code') {
      this.discountEndTime = endTime * 1000
    }
  }

  private async checkAlipaySignStatus() {
    try {
      await api.user.checkAlipaySignStatus()
      userService.updateUserInfo()
    } catch (error) {
      //
    }
  }

  public async alipayUnsign() {
    await api.user.alipayUnsign()
    userService.updateUserInfo()
  }

  private calcHeight() {
    // 检查当前视窗高度是否小于720，如果小于则需要缩放内容比例
    const height = window.innerHeight
    if (height < 710) {
      const scale = (height - 20) / 702
      this.scale = scale
    } else {
      this.scale = 1
    }
  }
}
